import Button from '@components/ui/Button';
import Toggle from '@components/ui/Toggle';
import { ModalShell } from '../shared';
import type { UnsyncedOrder } from '../../../../types/api.types';
import {
    ERROR_TYPE_OPTIONS,
    ERROR_TYPE_LABELS,
    getOrderHeaderId,
    type ErrorTypeOption,
} from './unsyncedOrderUtils';

interface DeleteUnsyncedOrdersDialogProps {
    isOpen: boolean;
    selectedOrders: UnsyncedOrder[];
    deleteErrorType: ErrorTypeOption | '';
    forceDelete: boolean;
    isLoading: boolean;
    onDeleteErrorTypeChange: (value: ErrorTypeOption | '') => void;
    onForceDeleteChange: (value: boolean) => void;
    onConfirm: () => void;
    onClose: () => void;
}

function WarningIcon() {
    return (
        <div className="w-10 h-10 rounded-full bg-[#fef2f2] flex items-center justify-center flex-shrink-0">
            <svg
                className="w-5 h-5 text-[#ef4444]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
            </svg>
        </div>
    );
}

export default function DeleteUnsyncedOrdersDialog({
    isOpen,
    selectedOrders,
    deleteErrorType,
    forceDelete,
    isLoading,
    onDeleteErrorTypeChange,
    onForceDeleteChange,
    onConfirm,
    onClose,
}: DeleteUnsyncedOrdersDialogProps) {
    const selectedCount = selectedOrders.length;
    const previewOrders = selectedOrders.slice(0, 3);
    const remainingCount = selectedCount - previewOrders.length;

    const footer = (
        <>
            <Button
                variant="secondary"
                className="flex-1"
                onClick={onClose}
                disabled={isLoading}
            >
                Cancel
            </Button>
            <Button
                variant="destructive"
                className="flex-1"
                isLoading={isLoading}
                onClick={onConfirm}
            >
                Delete orders
            </Button>
        </>
    );

    return (
        <ModalShell
            isOpen={isOpen}
            title={`Delete ${selectedCount} order${selectedCount !== 1 ? 's' : ''}?`}
            subtitle="This permanently removes orders from the sync queue. This cannot be undone."
            onClose={onClose}
            footer={footer}
            maxWidth="sm"
            elevation="nested"
        >
            <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-3">
                    <WarningIcon />
                    <p className="text-sm text-[#991b1b]">
                        Selected orders will be removed from POS sync queues on connected
                        terminals.
                    </p>
                </div>

                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-[#94a3b8] mb-2">
                        Selected orders
                    </p>
                    <ul className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] divide-y divide-[#e2e8f0]">
                        {previewOrders.map((order) => (
                            <li
                                key={order._id}
                                className="px-3 py-2 text-sm text-[#1e293b] truncate"
                                title={getOrderHeaderId(order)}
                            >
                                {getOrderHeaderId(order)}
                            </li>
                        ))}
                        {remainingCount > 0 && (
                            <li className="px-3 py-2 text-sm text-[#64748b]">
                                and {remainingCount} more
                            </li>
                        )}
                    </ul>
                </div>

                <div>
                    <label
                        htmlFor="delete-error-type"
                        className="block text-sm font-medium text-[#475569] mb-1.5"
                    >
                        Filter by issue type (optional)
                    </label>
                    <select
                        id="delete-error-type"
                        value={deleteErrorType}
                        onChange={(e) =>
                            onDeleteErrorTypeChange(e.target.value as ErrorTypeOption | '')
                        }
                        className="w-full h-10 px-3 text-sm bg-white border border-[#d1d5db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6e64f0] focus:border-[#6e64f0]"
                    >
                        <option value="">All issue types</option>
                        {ERROR_TYPE_OPTIONS.map((et) => (
                            <option key={et} value={et}>
                                {ERROR_TYPE_LABELS[et]}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-[#e2e8f0] px-3 py-3">
                    <div className="min-w-0">
                        <span className="text-sm font-medium text-[#1e293b]">Force delete</span>
                        <p className="text-xs text-[#64748b] mt-0.5">
                            Delete even if the order is still being processed.
                        </p>
                    </div>
                    <Toggle
                        value={forceDelete}
                        onChange={onForceDeleteChange}
                        label="Force delete"
                    />
                </div>
            </div>
        </ModalShell>
    );
}
